import React, { useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import DatePicker from "@/components/DatePicker";
import { Sparkles, X, Send, MessageSquare } from "lucide-react";

const PRIORITIES = [
  { key: "fair_distribution", label: "Fair Distribution of Hours" },
  { key: "minimize_overtime", label: "Minimize Overtime" },
  { key: "best_coverage", label: "Best Store Coverage" },
];
const PRIORITY_LEVELS = ["required", "high", "medium", "low", "ignore"];

export default function AiWizard({ employees, onApply, onClose, defaultWeek }) {
  const [step, setStep] = useState("form"); // form | preview | chat
  const [busy, setBusy] = useState(false);
  const [weekStart, setWeekStart] = useState(defaultWeek || new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState(employees.map((e) => e.id));
  const [priorities, setPriorities] = useState({ fair_distribution: "high", minimize_overtime: "high", best_coverage: "medium" });
  const [openHour, setOpenHour] = useState(10);
  const [closeHour, setCloseHour] = useState(22);
  const [maxHrs, setMaxHrs] = useState(40);
  const [minStaff, setMinStaff] = useState(2);
  const [result, setResult] = useState(null);

  const generate = async () => {
    setBusy(true);
    try {
      const coverage = Array.from({ length: 7 }, (_, i) => ({
        day: i, start: `${String(openHour).padStart(2, "0")}:00`, end: `${String(closeHour).padStart(2, "0")}:00`, min_staff: minStaff,
      }));
      const { data } = await api.post("/scheduling/generate", {
        week_start: weekStart,
        employee_ids: selected,
        coverage, priorities,
        max_weekly_hours: maxHrs,
        open_hour: openHour, close_hour: closeHour,
      });
      setResult(data);
      setStep("preview");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70" onClick={onClose}>
      <div className="hos-card p-6 max-w-3xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="ai-wizard">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2"><Sparkles className="text-[color:var(--gold)]"/><h3 className="font-heading text-2xl">AI Scheduling Assistant</h3></div>
          <div className="flex gap-2 items-center">
            <button className={`text-xs px-2 py-1 rounded ${step === "chat" ? "bg-[color:var(--gold)] text-[color:var(--bg-base)]" : "text-[color:var(--gold)] border border-[color:var(--gold)]"}`}
                    onClick={() => setStep(step === "chat" ? "form" : "chat")} data-testid="ai-chat-toggle"><MessageSquare size={12} className="inline mr-1"/> Ask</button>
            <button onClick={onClose}><X size={20}/></button>
          </div>
        </div>

        {step === "form" && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><label className="label-caps">Week starting (Mon)</label><DatePicker value={weekStart} onChange={setWeekStart} testId="ai-week"/></div>
              <div><label className="label-caps">Min staff / hour</label><input type="number" className="hos-input" value={minStaff} onChange={(e) => setMinStaff(Number(e.target.value))}/></div>
              <div><label className="label-caps">Store open</label><input type="number" className="hos-input" value={openHour} onChange={(e) => setOpenHour(Number(e.target.value))}/></div>
              <div><label className="label-caps">Store close</label><input type="number" className="hos-input" value={closeHour} onChange={(e) => setCloseHour(Number(e.target.value))}/></div>
              <div className="sm:col-span-2"><label className="label-caps">Max weekly hours per employee</label><input type="number" className="hos-input" value={maxHrs} onChange={(e) => setMaxHrs(Number(e.target.value))}/></div>
            </div>
            <div>
              <label className="label-caps">Employees</label>
              <div className="max-h-32 overflow-y-auto border border-[color:var(--border)] rounded-md p-2 space-y-1">
                {employees.map((e) => (
                  <label key={e.id} className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={selected.includes(e.id)} onChange={(ev) => setSelected(ev.target.checked ? [...selected, e.id] : selected.filter((x) => x !== e.id))}/>
                    {e.name} — {e.role || "—"}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label className="label-caps">Optimization Priorities</label>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                {PRIORITIES.map((p) => (
                  <div key={p.key}>
                    <div className="text-xs">{p.label}</div>
                    <select className="hos-input py-1" value={priorities[p.key]} onChange={(e) => setPriorities({ ...priorities, [p.key]: e.target.value })} data-testid={`ai-prio-${p.key}`}>
                      {PRIORITY_LEVELS.map((l) => <option key={l}>{l}</option>)}
                    </select>
                  </div>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-3">
              <button className="hos-btn-outline" onClick={onClose}>Cancel</button>
              <button className="hos-btn-primary flex items-center gap-2" onClick={generate} disabled={busy || selected.length === 0} data-testid="ai-generate-btn">
                <Sparkles size={16}/> {busy ? "Optimizing…" : "Generate Schedule"}
              </button>
            </div>
          </div>
        )}

        {step === "preview" && result && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
              <Stat k="Solver" v={result.solver_status}/>
              <Stat k="Score" v={`${result.score}/100`}/>
              <Stat k="Shifts" v={result.shifts.length}/>
              <Stat k="Unfilled" v={result.unfilled}/>
            </div>
            {result.score_breakdown && (
              <div className="text-xs text-[color:var(--text-secondary)] flex gap-4 flex-wrap">
                <span>Coverage: {result.score_breakdown.coverage}%</span>
                <span>Fairness: {result.score_breakdown.fairness}%</span>
                <span>Overtime: {result.score_breakdown.overtime}%</span>
              </div>
            )}
            {(result.warnings || []).length > 0 && (
              <div className="p-3 border border-[color:var(--warning)] bg-[color:var(--warning)]/10 rounded-lg text-sm">
                {result.warnings.map((w, i) => <div key={i}>{w}</div>)}
              </div>
            )}
            <div className="max-h-64 overflow-y-auto border border-[color:var(--border)] rounded-md">
              <table className="w-full text-xs">
                <thead className="text-left label-caps"><tr><th className="p-2">Date</th><th>Employee</th><th>Time</th><th>Role</th></tr></thead>
                <tbody>
                  {result.shifts.map((s, i) => (
                    <tr key={i} className="border-t border-[color:var(--border)]"><td className="p-1">{s.date}</td><td>{s.employee_name}</td><td>{s.start_time}–{s.end_time}</td><td>{s.role}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex justify-end gap-3">
              <button className="hos-btn-outline" onClick={() => setStep("form")}>Change & Regenerate</button>
              <button className="hos-btn-primary" onClick={() => { onApply(result.shifts, weekStart); toast.success("AI schedule applied as draft"); onClose(); }} data-testid="ai-apply-btn">Accept as Draft</button>
            </div>
          </div>
        )}

        {step === "chat" && <ChatPanel />}
      </div>
    </div>
  );
}

function Stat({ k, v }) {
  return <div><div className="label-caps">{k}</div><div className="text-xl font-heading">{v}</div></div>;
}

function ChatPanel() {
  const [messages, setMessages] = useState([{ role: "assistant", text: "Hi — I can explain the schedule, suggest changes, or answer coverage questions. What would you like to know?" }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    if (!input.trim()) return;
    const msg = input.trim();
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/scheduling/assistant", { message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply, provider: data.provider }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "AI assistant unavailable right now. The optimizer still works." }]);
    } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3">
      <div className="border border-[color:var(--border)] rounded-md p-3 max-h-80 overflow-y-auto space-y-2" data-testid="ai-chat-messages">
        {messages.map((m, i) => (
          <div key={i} className={m.role === "user" ? "text-right" : "text-left"}>
            <div className={`inline-block px-3 py-2 rounded-lg text-sm ${m.role === "user" ? "bg-[color:var(--gold)] text-[color:var(--bg-base)]" : "bg-[color:var(--bg-surface-2)]"}`}>
              {m.text}
            </div>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input className="hos-input flex-1" placeholder="e.g. 'Give Maria at least 30 hours'…" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} data-testid="ai-chat-input"/>
        <button className="hos-btn-primary" onClick={send} disabled={busy} data-testid="ai-chat-send"><Send size={14}/></button>
      </div>
      <div className="text-[10px] text-[color:var(--text-secondary)]">Powered by Gemini 2.5 Flash via Emergent Universal Key. Falls back gracefully when quota is exhausted.</div>
    </div>
  );
}
